import {
  apiV2ComprehensiveLabelListPost,
  apiV2DataMonitorAlarmRuleMonitoringDevicePagePost,
  apiV2DataMonitorAlarmRuleMonitoringDeviceRemovePost,
  V2DataMonitorAlarmRuleMonitoringDevicePagePostRequest,
  V2DataMonitorAlarmRuleMonitoringDevicePagePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import {
  Button,
  CustomFilter,
  EllipsisSpan,
  Form,
  Input,
  Modal,
  Paging,
  PopConfirm,
  Select,
  Table,
  usePaging,
  Checkbox,
} from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { Space } from 'antd';
import React, { useMemo, useState } from 'react';

interface MonitoringDeviceModalProps {
  open: boolean;
  ruleId?: number;
  readOnly?: boolean;
  readOnlySelectedDeviceIds?: number[];
  onClose: () => void;
}

type DeviceSearchParams = Omit<V2DataMonitorAlarmRuleMonitoringDevicePagePostRequest, 'ruleId' | 'page' | 'size'>;

type DeviceSearchFormValues = Omit<DeviceSearchParams, 'isThirdParty'> & {
  isThirdParty?: boolean;
};

type MonitoringDevice = NonNullable<V2DataMonitorAlarmRuleMonitoringDevicePagePostResponse['list']>[number];

const MonitoringDeviceModal: React.FC<MonitoringDeviceModalProps> = ({
  open,
  ruleId,
  readOnly = false,
  readOnlySelectedDeviceIds = [],
  onClose,
}) => {
  const [form] = Form.useForm<DeviceSearchFormValues>();
  const pagingInfo = usePaging(100);
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;
  const [searchParams, setSearchParams] = useState<DeviceSearchParams>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const { data: labelData } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 3 }), {
    ready: open,
  });

  const labelOptions = useMemo(
    () =>
      (labelData?.list ?? []).flatMap(item =>
        item.id === undefined || !item.name ? [] : [{ label: item.name, value: item.id }]
      ),
    [labelData]
  );

  const {
    data: deviceList = [],
    loading,
    refresh,
    cancel,
  } = useRequest(
    () => {
      const requestParams: V2DataMonitorAlarmRuleMonitoringDevicePagePostRequest = {
        ruleId,
        ...searchParams,
        page: pageOffset,
        size: pageSize,
      };

      return apiV2DataMonitorAlarmRuleMonitoringDevicePagePost(requestParams).then(response => {
        setTotalCount(response.total ?? 0);
        return response.list ?? [];
      });
    },
    {
      ready: open && ruleId !== undefined,
      refreshDeps: [open, ruleId, pageOffset, pageSize, searchParams, setTotalCount],
    }
  );

  const { runAsync: unbindDevices, loading: unbinding } = useRequest(
    (deviceIds: number[]) =>
      apiV2DataMonitorAlarmRuleMonitoringDeviceRemovePost({
        ruleId,
        deviceIds,
      }),
    { manual: true }
  );

  const removeDevices = async (deviceIds: number[]) => {
    if (ruleId === undefined || deviceIds.length === 0) return;

    await unbindDevices(deviceIds);
    setSelectedRowKeys(keys => keys.filter(key => !deviceIds.includes(Number(key))));
    refresh();
  };

  const onFinish = (values: DeviceSearchFormValues) => {
    const { isThirdParty, ...rest } = values;
    setSearchParams({ ...rest, isThirdParty: isThirdParty ? 1 : undefined });
    setPageOffset(1);
  };

  const onReset = () => {
    setSearchParams({});
    setPageOffset(1);
  };

  const handleClose = () => {
    cancel();
    form.resetFields();
    setSearchParams({});
    setSelectedRowKeys([]);
    setPageOffset(1);
    onClose();
  };

  const columns = [
    {
      title: '监听设备名称',
      dataIndex: 'deviceName',
      width: 180,
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '监听设备编号',
      dataIndex: 'deviceCode',
      width: 180,
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '所属类目',
      dataIndex: 'deviceTypeName',
      width: 160,
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '--'} />,
    },
    {
      title: '设备标签',
      dataIndex: 'deviceLabels',
      width: 220,
      ellipsis: { showTitle: true },
      render: (value?: string[]) => <EllipsisSpan value={value?.length ? value.join('、') : '--'} />,
    },
    {
      title: '是否为三方设备',
      dataIndex: 'isThirdParty',
      width: 140,
      render: (value?: number) => <EllipsisSpan value={value === undefined ? '--' : value === 1 ? '是' : '否'} />,
    },
    ...(!readOnly
      ? [
          {
            title: '操作',
            fixed: 'right' as const,
            width: 100,
            render: (_: unknown, record: MonitoringDevice) => (
              <PopConfirm
                title="确认移除？"
                description="移除后，该设备将不再监听当前报警规则。"
                okText="确定"
                cancelText="取消"
                onConfirm={() => (record.deviceId === undefined ? undefined : removeDevices([record.deviceId]))}
              >
                <Button type="link" disabled={unbinding || record.deviceId === undefined}>
                  移除
                </Button>
              </PopConfirm>
            ),
          },
        ]
      : []),
  ];

  const filters = (
    <CustomFilter<DeviceSearchFormValues>
      colSpan={6}
      form={form}
      onFinish={values => onFinish(values as DeviceSearchFormValues)}
      onReset={onReset}
    >
      <Form.Item name="deviceCodeOrName" label="监听编号/名称">
        <Input allowClear placeholder="请输入" />
      </Form.Item>
      <Form.Item name="deviceLabelIds" label="设备标签">
        <Select
          allowClear
          mode="multiple"
          showSearch
          optionFilterProp="label"
          placeholder="请选择设备标签"
          options={labelOptions}
        />
      </Form.Item>
      <Form.Item name="isThirdParty" valuePropName="checked">
        <Checkbox>是否为三方设备</Checkbox>
      </Form.Item>
    </CustomFilter>
  );

  const selectedDeviceIds = selectedRowKeys.map(Number).filter(Number.isFinite);

  const confirmBatchRemove = () => {
    if (selectedDeviceIds.length === 0) return;

    Modal.confirm({
      title: `确定移除已选择的 ${selectedDeviceIds.length} 台设备？`,
      content: '移除后，设备将不再监听当前报警规则。',
      okText: '确定',
      cancelText: '取消',
      onOk: () => removeDevices(selectedDeviceIds),
    });
  };

  return (
    <Modal open={open} title="监听设备" size="big" footer={null} destroyOnClose onCancel={handleClose}>
      {filters}
      {!readOnly && (
        <Space style={{ marginBottom: 10 }}>
          <Button loading={unbinding} disabled={selectedDeviceIds.length === 0} onClick={confirmBatchRemove}>
            批量移除
          </Button>
        </Space>
      )}
      <Table
        rowKey="deviceId"
        sticky
        scroll={{ x: 900, y: 350 }}
        loading={loading}
        columns={columns}
        dataSource={deviceList}
        pagination={false}
        rowSelection={
          readOnly
            ? {
                preserveSelectedRowKeys: true,
                selectedRowKeys: readOnlySelectedDeviceIds,
                getCheckboxProps: () => ({ disabled: true }),
              }
            : {
                preserveSelectedRowKeys: true,
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }
        }
        showSelectedCount={!readOnly}
        selectedCount={selectedRowKeys.length}
        bottomLeftExtra={
          readOnly ? (
            <div>
              <span>本次新增勾选 </span>
              <span style={{ color: 'var(--mx-error-color)' }}>0</span>
              <span> 项，总计 </span>
              <span style={{ color: 'var(--mx-error-color)' }}>{new Set(readOnlySelectedDeviceIds).size}</span>
              <span> 项</span>
            </div>
          ) : undefined
        }
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </Modal>
  );
};

export default MonitoringDeviceModal;
